import { AdjustmentMode } from '@app/shared/models/contracts/enums/shared-enums';
import { useEffect, useRef, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { FORM_FIELD_NAMES, getManualFieldName } from '../formFieldNames';
import { InputModeState } from '../types';
import { isEmpty } from '../utils';

export const useInputModeManager = (
  isProRata: boolean,
  ownershipIndex: number,
  inputModeState: InputModeState,
  fieldNames: {
    adjustmentAmountField: string;
    adjustmentSharesField: string;
    closingAmountField: string;
    closingSharesField: string;
  }
) => {
  const { setValue, watch } = useFormContext();

  const adjustmentAmount = watch(fieldNames.adjustmentAmountField);
  const adjustmentShares = watch(fieldNames.adjustmentSharesField);
  const closingAmount = watch(fieldNames.closingAmountField);
  const closingShares = watch(fieldNames.closingSharesField);

  const prevValues = useRef({
    adjustmentAmount,
    adjustmentShares,
    closingAmount,
    closingShares,
  });

  const clearOppositeFields = useMemo(() => {
    return (newMode: AdjustmentMode) => {
      const isSettingAdjustment = newMode === AdjustmentMode.Relative;

      if (isProRata) {
        if (isSettingAdjustment) {
          setValue(FORM_FIELD_NAMES.PRO_RATA_CLOSING_AMOUNT, null);
          setValue(FORM_FIELD_NAMES.PRO_RATA_CLOSING_SHARES, null);
        } else {
          setValue(FORM_FIELD_NAMES.PRO_RATA_AMOUNT, null);
          setValue(FORM_FIELD_NAMES.PRO_RATA_SHARES, null);
        }

        setValue(FORM_FIELD_NAMES.PRO_RATA_AMOUNT_INPUT_MODE, newMode);
        setValue(FORM_FIELD_NAMES.PRO_RATA_SHARES_INPUT_MODE, newMode);
      } else {
        if (isSettingAdjustment) {
          setValue(
            getManualFieldName(ownershipIndex, FORM_FIELD_NAMES.MANUAL_CLOSING_AMOUNT),
            null
          );
          setValue(
            getManualFieldName(ownershipIndex, FORM_FIELD_NAMES.MANUAL_CLOSING_SHARES),
            null
          );
        } else {
          setValue(getManualFieldName(ownershipIndex, FORM_FIELD_NAMES.MANUAL_AMOUNT), null);
          setValue(getManualFieldName(ownershipIndex, FORM_FIELD_NAMES.MANUAL_SHARES), null);
        }

        setValue(
          getManualFieldName(ownershipIndex, FORM_FIELD_NAMES.MANUAL_AMOUNT_INPUT_MODE),
          newMode
        );
        setValue(
          getManualFieldName(ownershipIndex, FORM_FIELD_NAMES.MANUAL_SHARES_INPUT_MODE),
          newMode
        );
      }
    };
  }, [setValue, isProRata, ownershipIndex]);

  useEffect(() => {
    const adjustmentEmpty = isEmpty(adjustmentAmount) && isEmpty(adjustmentShares);
    const closingEmpty = isEmpty(closingAmount) && isEmpty(closingShares);

    if (adjustmentEmpty && closingEmpty) {
      if (isProRata) {
        setValue(FORM_FIELD_NAMES.PRO_RATA_AMOUNT_INPUT_MODE, null);
        setValue(FORM_FIELD_NAMES.PRO_RATA_SHARES_INPUT_MODE, null);
      } else {
        setValue(
          getManualFieldName(ownershipIndex, FORM_FIELD_NAMES.MANUAL_AMOUNT_INPUT_MODE),
          null
        );
        setValue(
          getManualFieldName(ownershipIndex, FORM_FIELD_NAMES.MANUAL_SHARES_INPUT_MODE),
          null
        );
      }
      return;
    }

    const isInitialModeState = !inputModeState.amountMode && !inputModeState.sharesMode;

    if (isInitialModeState) {
      if (!adjustmentEmpty && closingEmpty) {
        // changing mode to adjustment mode
        clearOppositeFields(AdjustmentMode.Relative);
      } else if (adjustmentEmpty && !closingEmpty) {
        // changing mode to closing mode
        clearOppositeFields(AdjustmentMode.Absolute);
      }

      return;
    }

    const hasDifferentModes =
      inputModeState.amountMode &&
      inputModeState.sharesMode &&
      inputModeState.amountMode !== inputModeState.sharesMode;

    if (hasDifferentModes) {
      // If modes are different, prioritize based on which field was changed last
      if (
        prevValues.current.adjustmentAmount !== adjustmentAmount ||
        prevValues.current.adjustmentShares !== adjustmentShares
      ) {
        clearOppositeFields(inputModeState.amountMode!);
      } else if (
        prevValues.current.closingAmount !== closingAmount ||
        prevValues.current.closingShares !== closingShares
      ) {
        clearOppositeFields(inputModeState.sharesMode!);
      }
    }

    prevValues.current = {
      adjustmentAmount,
      adjustmentShares,
      closingAmount,
      closingShares,
    };
  }, [
    adjustmentAmount,
    adjustmentShares,
    closingAmount,
    closingShares,
    isProRata,
    ownershipIndex,
    inputModeState,
    setValue,
    clearOppositeFields,
  ]);
};
